import { Menu, MenuItem } from '@mui/material';
import DataTable from 'react-data-table-component';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import './Domains.css';
import socket from '../../../socket';
import { getSalesAccounts } from '../../../store/Integration';
import MailboxDrawer from '../../../components/Zapmail/MailboxDrawer';
import DomainPurchaseWizard from '../../../components/Zapmail/DomainPurchaseWizard';
import TableAnimationFiles from '../../../components/Animations/TableAnimationFiles.js';
import { getZapmailPlan, getZapmailDomains, getZapmailOrders, getZapmailOrder, retryZapmailOrder, renewZapmailDomain, deleteZapmailDraft } from '../../../store/Zapmail';

// Domains panel (Settings > Integrations > Domains) — Done-For-You purchased domains + provisioning status
const Domains = () => {
    const dispatch = useDispatch();
    const { plan, domains, orders, activeOrder, loading } = useSelector(state => state['Zapmail']) || {};

    const [ui, setUi] = useState(INITIAL_UI);
    const updateUi = (patch) => setUi(prev => ({ ...prev, ...patch }));

    useEffect(() => {
        dispatch(getZapmailPlan());
        dispatch(getZapmailDomains());
        dispatch(getZapmailOrders());
        dispatch(getSalesAccounts());
        const params = new URLSearchParams(window['location']['search']);
        const orderId = params.get('order');
        if (orderId && params.get('status') === 'success') dispatch(getZapmailOrder(orderId));
        if (params.get('buy') === '1') updateUi({ wizardOpen: true, buySalesAccountId: params.get('salesAccountId') || null });
    }, [dispatch]);

    // Socket-driven provisioning updates — the backend emits zapmail:mailbox-ready per activated
    // mailbox and zapmail:order-updated after every fulfillment pass, so no interval polling needed
    useEffect(() => {
        const onOrderEvent = (payload) => {
            if (payload?.['orderId']) dispatch(getZapmailOrder(payload['orderId']));
            if (['completed', 'partially_failed', 'failed'].includes(payload?.['status'])) { dispatch(getZapmailOrders()); dispatch(getZapmailDomains()); }
        };
        socket.on('zapmail:order-updated', onOrderEvent);
        socket.on('zapmail:mailbox-ready', onOrderEvent);
        return () => { socket.off('zapmail:order-updated', onOrderEvent); socket.off('zapmail:mailbox-ready', onOrderEvent); };
    }, [dispatch]);

    // Keep the wizard state in the URL (?buy=1) so a reload reopens it; the wizard itself offers
    // to resume the latest unpaid order from the database
    const openWizard = () => {
        const params = new URLSearchParams(window['location']['search']);
        params.set('buy', '1');
        window.history.replaceState(null, '', `${window['location']['pathname']}?${params}`);
        updateUi({ wizardOpen: true });
    };

    const closeWizard = () => {
        const params = new URLSearchParams(window['location']['search']);
        params.delete('buy');
        params.delete('salesAccountId');
        window.history.replaceState(null, '', `${window['location']['pathname']}?${params}`);
        updateUi({ wizardOpen: false, buySalesAccountId: null });
        dispatch(getZapmailOrders());
        dispatch(getZapmailDomains());
    };

    // Open the wizard directly on the shared team draft (review-and-pay flow)
    const openDraftWizard = (draftId) => {
        const params = new URLSearchParams(window['location']['search']);
        params.set('buy', '1');
        params.set('draft', draftId);
        window.history.replaceState(null, '', `${window['location']['pathname']}?${params}`);
        updateUi({ wizardOpen: true });
    };

    const handleDiscardDraft = () => {
        const params = new URLSearchParams(window['location']['search']);
        params.delete('draft');
        window.history.replaceState(null, '', `${window['location']['pathname']}?${params}`);
        dispatch(deleteZapmailDraft(() => dispatch(getZapmailOrders())));
    };

    // The workspace's shared draft rides in the orders list as a 'draft'-status order
    const teamDraft = (orders || []).find(order => order['status'] === 'draft' && (order['items'] || []).some(item => item['kind'] === 'domain'));

    // Latest unfinished order surfaces as the provisioning card even without the success redirect
    const inflightOrder = activeOrder || (orders || []).find(order => ['paid', 'provisioning', 'partially_failed'].includes(order['status']));

    const handleRetry = () => dispatch(retryZapmailOrder(inflightOrder['_id'], () => dispatch(getZapmailOrder(inflightOrder['_id']))));

    const handleMenuOpen = (event, domain) => updateUi({ anchorEl: event['currentTarget'], menuDomain: domain });
    const handleMenuClose = () => updateUi({ anchorEl: null, menuDomain: null });
    const handleRenew = () => {
        const domain = ui['menuDomain'];
        handleMenuClose();
        if (domain) dispatch(renewZapmailDomain(domain['_id']));
    };

    const columns = buildColumns(handleMenuOpen);
    const featureEnabled = plan ? plan['enabled'] !== false : true;

    // Full-screen stepper (ImportWizard-style) replaces the tab content while buying
    if (ui['wizardOpen']) return <DomainPurchaseWizard salesAccountId={ui['buySalesAccountId']} onClose={closeWizard} />;

    return (
        <div className="domains-tab">
            <div className="domains-tab-header">
                <div className="domains-tab-header-left">
                    <h2 className="domains-tab-title">Domains &amp; Mailboxes</h2>
                    <p className="records-title">{(domains || []).length} records</p>
                </div>
                <div className="domains-tab-header-right">
                    <button className="btn-style-border" onClick={openWizard} disabled={!featureEnabled}>＋ Buy Domain &amp; Mailboxes</button>
                </div>
            </div>

            {inflightOrder && (
                <div className="domains-tab-order-card">
                    <div className="domains-tab-order-icon"><i className={`fa ${ORDER_ICONS[inflightOrder['status']] || 'fa-cog fa-spin'}`}></i></div>
                    <div className="domains-tab-order-body">
                        <div className="domains-tab-order-head">
                            <span className="domains-tab-order-title">{orderStatusTitle(inflightOrder['status'])}</span>
                            <div className="domains-tab-order-right">
                                {inflightOrder['status'] === 'partially_failed' && <button className="btn-style-border" onClick={handleRetry}>Retry</button>}
                                <span className="domains-tab-order-percent">{orderProgress(inflightOrder)}%</span>
                            </div>
                        </div>
                        <div className="domains-tab-progress">
                            <div className="domains-tab-progress-fill" style={{ width: `${orderProgress(inflightOrder)}%` }} />
                        </div>
                        <div className="domains-tab-order-meta">
                            {orderMailboxSummary(inflightOrder)}
                            {['paid', 'provisioning'].includes(inflightOrder['status']) && <span> · Google mailbox setup usually takes up to 2 hours</span>}
                            {inflightOrder['lastError'] === 'PAYMENT_PAST_DUE' && <span className="domains-tab-order-error"> · Payment failed — update billing to keep these mailboxes active</span>}
                        </div>
                    </div>
                </div>
            )}

            {teamDraft && (
                <div className="domains-tab-draft-card">
                    <div className="domains-tab-order-icon"><i className="fa fa-pencil-square-o"></i></div>
                    <div className="domains-tab-draft-body">
                        <span className="domains-tab-draft-title">Purchase draft in progress</span>
                        <span className="domains-tab-draft-meta">{draftMeta(teamDraft)}</span>
                    </div>
                    <div className="domains-tab-draft-btns">
                        <button className="btn-style-border" onClick={handleDiscardDraft}>Discard</button>
                        <button className="btn-style-new" onClick={() => openDraftWizard(teamDraft['_id'])}>Review &amp; continue</button>
                    </div>
                </div>
            )}

            {(domains || []).length === 0 && !loading ? (
                <div className="domains-tab-empty">
                    <i className="fa fa-globe domains-tab-empty-icon"></i>
                    <h3 className="domains-tab-empty-title">Set up your email infrastructure in minutes</h3>
                    <p className="domains-tab-empty-text">Buy a domain, add Google mailboxes, and we handle registration, DNS, warmup and connection — done for you.</p>
                    <button className="btn-style-border" onClick={openWizard} disabled={!featureEnabled}>Buy Domain &amp; Mailboxes</button>
                </div>
            ) : (
                <div className="domains-tab-table-card table-container">
                    {/* Reason: fixedHeader caps the body at the viewport height (matches properties.js) — at 125%/150% zoom the rows scroll vertically inside the card instead of being cut off below the fold */}
                    <DataTable className="domains-table table-sellagent" columns={columns} data={domains || []} progressPending={loading} progressComponent={<TableAnimationFiles />} pagination highlightOnHover pointerOnHover noHeader
                        fixedHeader fixedHeaderScrollHeight="calc(100vh - 220px)"
                        expandableRows expandOnRowClicked expandableRowsComponent={DomainMailboxes} expandableRowDisabled={row => !(row['mailboxes'] || []).length} />
                </div>
            )}

            <Menu anchorEl={ui['anchorEl']} open={Boolean(ui['anchorEl'])} onClose={handleMenuClose}>
                <MenuItem onClick={handleRenew} disabled={!ui['menuDomain']?.['zapmailDomainId']}>Renew domain</MenuItem>
            </Menu>
        </div>
    );
};

const INITIAL_UI = { wizardOpen: false, buySalesAccountId: null, anchorEl: null, menuDomain: null };

// Draft card line: who started it, what's in it, who touched it last and when
const draftMeta = (draft) => {
    const name = (user) => user && typeof user === 'object' ? (`${user['firstName'] || ''} ${user['lastName'] || ''}`.trim() || 'A teammate') : 'A teammate';
    const domains = (draft['items'] || []).filter(item => item['kind'] === 'domain').length;
    const mailboxes = (draft['items'] || []).filter(item => item['kind'] === 'mailbox_plan').reduce((count, item) => count + (item['mailboxes']?.length || 0), 0);
    return `Started by ${name(draft['userId'])} · ${domains} domain${domains === 1 ? '' : 's'}, ${mailboxes} mailbox${mailboxes === 1 ? '' : 'es'} · last edited by ${name(draft['updatedBy'] || draft['userId'])} on ${new Date(draft['updatedAt']).toLocaleString()}`;
};

const formatPrice = (cents) => cents ? `$${(cents / 100).toFixed(2)}` : '—';
const formatDate = (value) => value ? new Date(value).toLocaleDateString() : '—';

const STATUS_TONES = { active: 'active', pending: 'pending', expired: 'expired', failed: 'failed' };

const orderStatusTitle = (status) => ({
    paid: 'Payment received — starting setup...',
    provisioning: 'Setting up your domains and mailboxes...',
    completed: 'Setup complete — your mailboxes are connected',
    partially_failed: 'Setup finished with issues — failed items were refunded',
    failed: 'Setup failed — our team has been notified'
}[status] || 'Order in progress');

// Mirrors the server-side progress math: each item + each mailbox is one unit of work
const orderProgress = (order) => {
    let total = 0, done = 0;
    for (const item of order['items'] || []) {
        total += 1;
        if (['completed', 'purchased', 'refunded', 'failed'].includes(item['status'])) done += 1;
        for (const mailbox of item['mailboxes'] || []) { total += 1; if (mailbox['zapmailMailboxId']) done += 1; }
    }
    return total ? Math.round((done / total) * 100) : 0;
};

const orderMailboxSummary = (order) => {
    const mailboxes = (order['items'] || []).flatMap(item => item['mailboxes'] || []);
    if (!mailboxes.length) return `${(order['items'] || []).length} item(s)`;
    const ready = mailboxes.filter(m => m['zapmailMailboxId']).length;
    return `${ready}/${mailboxes.length} mailboxes ready`;
};

const ORDER_ICONS = { paid: 'fa-cog fa-spin', provisioning: 'fa-cog fa-spin', completed: 'fa-check', partially_failed: 'fa-exclamation-triangle', failed: 'fa-exclamation-triangle' };

// data-tag="allowRowEvents" lets clicks on custom cell content reach the row so expandOnRowClicked
// can toggle the mailbox list; the menu column opts out via ignoreRowClick
const buildColumns = (onMenuOpen) => [
    // Reason: explicit minWidths (instead of rdt's blanket 100px default) guarantee each column enough
    // room at 125%/150% zoom — the domain never wraps mid-word and the PURCHASED header never truncates
    {
        name: 'Domain', selector: row => row['domainName'], sortable: true, grow: 2, minWidth: '200px', cell: row => (
            <div className="domains-table-domain" data-tag="allowRowEvents">
                <span className="domains-table-domain-icon" data-tag="allowRowEvents"><i className="fa fa-globe" data-tag="allowRowEvents"></i></span>
                <span className="domains-table-name" data-tag="allowRowEvents">{row['domainName']}</span>
            </div>
        )
    },
    { name: 'Status', selector: row => row['status'], sortable: true, minWidth: '90px', cell: row => <span className={`domains-table-badge ${STATUS_TONES[row['status']] || 'pending'}`} data-tag="allowRowEvents"><span className="domains-table-badge-dot" data-tag="allowRowEvents">•</span> {row['status']}</span> },
    { name: 'Mailboxes', selector: row => (row['mailboxes'] || []).length, sortable: true, minWidth: '104px', maxWidth: '130px', cell: row => <span className="domains-table-count-pill" data-tag="allowRowEvents">{(row['mailboxes'] || []).length} mailbox{(row['mailboxes'] || []).length === 1 ? '' : 'es'}</span> },
    { name: 'Purchased', selector: row => row['purchasedAt'], sortable: true, minWidth: '104px', format: row => formatDate(row['purchasedAt']) },
    { name: 'Expires', selector: row => row['expiresAt'], sortable: true, minWidth: '86px', format: row => formatDate(row['expiresAt']) },
    { name: 'Renewal', selector: row => row['retailRenewPrice'], minWidth: '76px', format: row => { const price = row['retailRenewPrice'] || row['renewPrice']; return price ? `${formatPrice(price)}/yr` : '—'; } },
    { name: '', width: '52px', ignoreRowClick: true, cell: row => <MoreVertIcon className="domains-table-menu-icon" onClick={(event) => onMenuOpen(event, row)} /> }
];

// Expanded row — the Done-For-You mailboxes on this domain. Clicking a row opens the management
// drawer (credentials / edit / sales account / subscription); the assign pill jumps to that tab.
const DomainMailboxes = ({ data }) => {
    const dispatch = useDispatch();
    const [ui, setUi] = useState(INITIAL_MAILBOX_UI);
    const updateBoxUi = (patch) => setUi(prev => ({ ...prev, ...patch }));
    const openDrawer = (box, tab) => updateBoxUi({ drawerBox: box, drawerTab: tab });

    return (
        <div className="domains-mailbox-list">
            {(data['mailboxes'] || []).map(box => (
                <div key={box['email']} className="domains-mailbox-row" onClick={() => openDrawer(box, 0)}>
                    {box['profilePicture']
                        ? <img className="domains-mailbox-avatar-img" src={box['profilePicture']} alt="" />
                        : <span className="domains-mailbox-avatar">{initials(box['name'] || box['email'])}</span>}
                    <div className="domains-mailbox-info">
                        <span className="domains-mailbox-email">{box['email']}</span>
                        <span className="domains-mailbox-name">{box['name'] || '—'}</span>
                    </div>
                    {box['status'] === 'pending_setup'
                        ? <span className="domains-mailbox-eta">Google is creating this mailbox — usually takes up to 2 hours</span>
                        : <>
                            <div className="domains-mailbox-meta"><span className="domains-mailbox-meta-label">Created</span><span className="domains-mailbox-meta-value">{formatDate(box['createdAt'])}</span></div>
                            <div className="domains-mailbox-meta"><span className="domains-mailbox-meta-label">Renews</span><span className="domains-mailbox-meta-value">{formatDate(box['renewsAt'])}</span></div>
                        </>}
                    <span className={`domains-table-badge ${MAILBOX_TONES[box['status']] || 'pending'}`}><span className="domains-table-badge-dot">•</span> {MAILBOX_LABELS[box['status']] || box['status']}</span>
                    {box['salesAccountId']
                        ? <span className="domains-mailbox-assigned-pill" onClick={(event) => { event.stopPropagation(); openDrawer(box, 1); }}><i className="fa fa-user"></i> {box['salesAccountName'] || 'Assigned'}</span>
                        : <span className="domains-mailbox-assign-now" onClick={(event) => { event.stopPropagation(); openDrawer(box, 1); }}>＋ Assign now</span>}
                    <i className="fa fa-angle-right domains-mailbox-chevron"></i>
                </div>
            ))}
            <MailboxDrawer open={Boolean(ui['drawerBox'])} mailbox={ui['drawerBox']} initialTab={ui['drawerTab']} onClose={() => updateBoxUi({ drawerBox: null })} onChanged={() => dispatch(getZapmailDomains())} />
        </div>
    );
};

const INITIAL_MAILBOX_UI = { drawerBox: null, drawerTab: 0 };

const MAILBOX_LABELS = { pending_setup: 'In Progress', active: 'Active', needs_reconnect: 'Needs Reconnect', inactive: 'Inactive' };
const MAILBOX_TONES = { pending_setup: 'pending', active: 'active', needs_reconnect: 'failed', inactive: 'expired' };

const initials = (value) => String(value || '?').trim().split(/\s+/).map(word => word[0]).slice(0, 2).join('').toUpperCase();

export default Domains;
